import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";

const ROLES = ["content_admin", "exam_manager", "question_manager", "marketing_manager", "support_manager", "finance_manager", "seo_manager", "super_admin"];

export default function AdminSystem() {
  const [tab, setTab] = useState("Audit");
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-6">System</h1>
      <div className="flex gap-1 mb-6 border-b border-line">
        {["Audit", "Admin Users", "Roles"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm border-b-2 -mb-px ${tab === t ? "border-ember text-ember" : "border-transparent text-content-secondary"}`} data-testid={`system-tab-${t.toLowerCase().replace(" ", "-")}`}>{t}</button>
        ))}
      </div>
      {tab === "Audit" && <Audit />}
      {tab === "Admin Users" && <AdminUsers />}
      {tab === "Roles" && <Roles />}
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="card-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-line text-left text-content-muted">
          <th className="px-4 py-3 font-medium">Admin</th><th className="px-4 py-3 font-medium">Action</th>
          <th className="px-4 py-3 font-medium">Object</th><th className="px-4 py-3 font-medium">When</th>
        </tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-b border-line/50">
              <td className="px-4 py-3 text-content-secondary">{l.admin_email}</td>
              <td className="px-4 py-3"><span className="font-mono text-xs bg-card-hover px-2 py-0.5 rounded">{l.action}</span></td>
              <td className="px-4 py-3 text-content-secondary">{l.object_type} {l.object_id ? `#${String(l.object_id).slice(0, 6)}` : ""}</td>
              <td className="px-4 py-3 text-content-muted text-xs">{new Date(l.created_at).toLocaleString("en-IN")}</td>
            </tr>
          ))}
          {logs.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-content-muted">No audit logs yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "content_admin" });
  const load = () => api.get("/admin/admin-users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/admin/admin-users", form); toast.success("Admin created"); setOpen(false); setForm({ role: "content_admin" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="btn-ember rounded-full" onClick={() => setOpen(true)} data-testid="admin-user-add">New Admin</Button></div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-content-muted"><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Role</th></tr></thead>
          <tbody>
            {users.map((u) => <tr key={u.id} className="border-b border-line/50"><td className="px-4 py-3">{u.name}</td><td className="px-4 py-3 text-content-secondary">{u.email}</td><td className="px-4 py-3"><span className="font-mono text-xs bg-ember-soft text-ember px-2 py-0.5 rounded">{u.role}</span></td></tr>)}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-line max-w-md">
          <DialogHeader><DialogTitle>New Admin User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ink border-line" data-testid="admin-user-name" />
            <Input placeholder="Email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-ink border-line" data-testid="admin-user-email" />
            <Input placeholder="Password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-ink border-line" data-testid="admin-user-password" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 rounded-md bg-ink border border-line px-3 text-sm">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <DialogFooter><Button variant="outline" className="border-line" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-ember" onClick={save} data-testid="admin-user-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Roles() {
  const [roles, setRoles] = useState([]);
  useEffect(() => { api.get("/admin/roles").then((r) => setRoles(r.data)).catch(() => {}); }, []);
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {roles.map((r) => (
        <div key={r.role} className="card-surface p-5">
          <div className="font-heading font-semibold text-sm mb-2">{r.role}</div>
          <div className="flex flex-wrap gap-1.5">
            {r.permissions.map((p) => <span key={p} className="text-[11px] font-mono bg-card-hover px-2 py-0.5 rounded">{p === "*" ? "all access" : p}</span>)}
          </div>
        </div>
      ))}
    </div>
  );
}
